import { useState } from 'react'
import type { Integration, SettingValue } from '../lib/types'

interface Props {
  integration: Integration
  pending: boolean
  error: string | null
  saved: boolean
  onSave: (settings: Record<string, SettingValue>, clearSettings: string[]) => void
}

const asText = (value: SettingValue | undefined) => (value === null || value === undefined ? '' : String(value))

/**
 * Settings are merged server-side: only changed keys are sent. A stored secret is never overwritten by
 * accident, because the API ignores a null for a secret unless the key is also named in clearSettings,
 * which only the Wissen button does.
 */
export default function SettingsForm({ integration, pending, error, saved, onSave }: Props) {
  const schema = integration.settingsSchema ?? {}
  const stored = integration.settings ?? {}
  const storedSecrets = integration.storedSecrets ?? []

  const [draft, setDraft] = useState<Record<string, string>>({})
  const [secrets, setSecrets] = useState<Record<string, string>>({})
  const [cleared, setCleared] = useState<ReadonlySet<string>>(new Set())
  const [editing, setEditing] = useState<ReadonlySet<string>>(new Set())

  const keys = Object.keys(schema)
  if (keys.length === 0) return <p className="muted small">Deze koppeling heeft geen instellingen.</p>

  const changes: Record<string, SettingValue> = {}
  for (const key of keys) {
    const field = schema[key]
    if (field?.secret) {
      if ((secrets[key] ?? '') !== '') changes[key] = secrets[key] as string
      continue
    }
    const next = draft[key]
    if (next === undefined) continue
    if (next !== asText(stored[key])) changes[key] = next.trim() === '' ? null : next
  }
  const clearSettings = keys.filter((key) => schema[key]?.secret && cleared.has(key))
  const dirty = Object.keys(changes).length > 0 || clearSettings.length > 0

  const toggle = (set: ReadonlySet<string>, key: string, on: boolean) => {
    const next = new Set(set)
    if (on) next.add(key)
    else next.delete(key)
    return next
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        if (!dirty) return
        onSave(changes, clearSettings)
        setDraft({})
        setSecrets({})
        setCleared(new Set())
        setEditing(new Set())
      }}
    >
      <div className="grid cols-2">
        {keys.map((key) => {
          const field = schema[key]
          if (!field) return null
          const inputId = `setting-${integration.id}-${key}`
          const isStoredSecret = storedSecrets.includes(key)
          const locked = Boolean(field.secret) && isStoredSecret && !editing.has(key) && !cleared.has(key)

          return (
            <div className="field" key={key}>
              <label htmlFor={inputId}>{field.label}</label>
              {locked ? (
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <input id={inputId} type="text" readOnly value="ingesteld" style={{ flex: 1 }} />
                  <button type="button" className="btn small" onClick={() => setEditing((set) => toggle(set, key, true))}>
                    Vervangen
                  </button>
                  <button type="button" className="btn small danger" onClick={() => setCleared((set) => toggle(set, key, true))}>
                    Wissen
                  </button>
                </div>
              ) : field.secret ? (
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <input
                    id={inputId}
                    type="password"
                    autoComplete="new-password"
                    placeholder={cleared.has(key) ? 'wordt gewist bij opslaan' : 'nog niet ingesteld'}
                    value={secrets[key] ?? ''}
                    disabled={cleared.has(key)}
                    style={{ flex: 1 }}
                    onChange={(event) => setSecrets((current) => ({ ...current, [key]: event.target.value }))}
                  />
                  {(editing.has(key) || cleared.has(key)) && (
                    <button
                      type="button"
                      className="btn small"
                      onClick={() => {
                        setEditing((set) => toggle(set, key, false))
                        setCleared((set) => toggle(set, key, false))
                        setSecrets((current) => ({ ...current, [key]: '' }))
                      }}
                    >
                      Annuleren
                    </button>
                  )}
                </div>
              ) : typeof stored[key] === 'boolean' ? (
                <label className="check">
                  <input
                    id={inputId}
                    type="checkbox"
                    checked={(draft[key] ?? String(stored[key])) === 'true'}
                    onChange={(event) => setDraft((current) => ({ ...current, [key]: String(event.target.checked) }))}
                  />
                  aan
                </label>
              ) : (
                <input
                  id={inputId}
                  type="text"
                  value={draft[key] ?? asText(stored[key])}
                  onChange={(event) => setDraft((current) => ({ ...current, [key]: event.target.value }))}
                />
              )}
              {field.help && <span className="help">{field.help}</span>}
            </div>
          )
        })}
      </div>
      <div className="actions">
        <button type="submit" className="btn primary" disabled={!dirty || pending}>
          {pending ? 'Opslaan…' : 'Instellingen opslaan'}
        </button>
        {error && <span className="inline-error">{error}</span>}
        {saved && !pending && !error && <span className="inline-ok">Opgeslagen</span>}
      </div>
    </form>
  )
}
